<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Button } from '@/components/ui/button'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { ChevronDown } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation.ts'
  import { type CustomerCase, RaMicroExportIntegrationTypeEnum } from '@/types/generated'
  import ActaportCollisionModal from '@/components/app/cases/ActaportCollisionModal.vue'

  const props = defineProps<{
    caseRecord: CustomerCase | null
  }>()

  const { t } = useTranslation({
    en: {
      conflictOfInterest: 'Conflict of Interest',
      createCase: 'Create {integration} Case',
      exportFile: 'Export {integration} File',
      createCaseGeneral: 'Akte anlegen'
    },
    de: {
      conflictOfInterest: 'Interessenkollision',
      createCase: '{integration} Akte anlegen',
      exportFile: '{integration} Datei exportieren',
      createCaseGeneral: 'Akte anlegen'
    },
    fr: {
      conflictOfInterest: "Conflit d'intérêts",
      createCase: 'Créer un dossier {integration}',
      exportFile: 'Exporter le fichier {integration}',
      createCaseGeneral: 'Créer un dossier'
    }
  })

  // Force reactivity with a trigger
  const reactivityTrigger = ref(0)

  // Integration enabled flags
  const integrations = computed(() => {
    // Access the trigger to ensure reactivity
    reactivityTrigger.value

    const org = props.caseRecord?.org
    return {
      lawFirmSoftwareExport: org?.is_law_firm_software_exportation_enabled || false,
      actaport: org?.is_actaport_export_integration_enabled || false,
      advolux: org?.is_advolux_export_integration_enabled || false,
      raMicro: org?.is_ra_micro_export_integration_enabled || false,
      advoware: org?.is_advoware_export_integration_enabled || false,
      kleos: org?.is_kleos_integration_enabled || false,
      jurNodes: org?.is_jurnodes_export_integration_enabled || false,
      datev: org?.is_datev_export_integration_enabled || false
    }
  })

  // Watch for case record changes to force reactivity
  watch(
    () => props.caseRecord?.org,
    (newOrg, oldOrg) => {
      if (newOrg !== oldOrg) {
        // Trigger reactivity
        reactivityTrigger.value++
      }
    },
    { deep: true, immediate: true }
  )

  // Integration states - track if cases already exist
  const integrationStates = computed(() => {
    // Access the trigger to ensure reactivity
    reactivityTrigger.value

    const caseRecord = props.caseRecord
    return {
      actaport: {
        caseId: caseRecord?.actaport_case_id,
        hasCase: !!caseRecord?.actaport_case_id
      },
      advoware: {
        caseId: caseRecord?.advoware_case_id,
        hasCase: !!caseRecord?.advoware_case_id
      },
      kleos: {
        caseId: caseRecord?.kleos_reference,
        hasCase: !!caseRecord?.kleos_reference
      }
    }
  })

  // RA-MICRO Oma can be either downloaded or sent via email
  const isOmaDownload = computed(() => {
    // Access the trigger to ensure reactivity
    reactivityTrigger.value

    return (
      props.caseRecord?.org?.ra_micro_export_integration_type ===
      RaMicroExportIntegrationTypeEnum.DOWNLOAD
    )
  })

  // Modal states
  const showActaportCollisionModal = ref(false)

  // Open Actaport collision check modal
  const openActaportCollisionModal = () => {
    showActaportCollisionModal.value = true
  }

  const exportMandate = (integration: string) => {
    if (!props.caseRecord?.uuid) return

    const host = import.meta.env.VITE_HOST || ''
    const caseUuid = props.caseRecord.uuid
    window.location.href = `${host}/app/integrations/${integration}/export/${caseUuid}`
  }

  const openActaportCase = (caseId: string | null | undefined) => {
    if (caseId) {
      // Replace slash with hyphen in the case ID
      const formattedCaseId = caseId.replace('/', '-')
      window.open(`https://app.actaport.de/akte/${formattedCaseId}/dashboard`, '_blank')
    }
  }

  // Count enabled integrations to determine if dropdown should be shown
  const enabledIntegrationsCount = computed(() => {
    return Object.values(integrations.value).filter(Boolean).length
  })

  // Show dropdown when 2 or more integrations are enabled
  const shouldShowDropdown = computed(() => {
    return enabledIntegrationsCount.value >= 2
  })

  // Get integration display information for dropdown items
  const getIntegrationDisplayInfo = (integration: string) => {
    switch (integration) {
      case 'actaport':
        return {
          name: 'Actaport',
          logo: 'https://s3-eu-central-1.ionoscloud.com/jupus/images/actaport_logo.png',
          bgColor: 'bg-gray-900',
          hoverColor: 'hover:bg-black',
          textColor: 'text-white'
        }
      case 'advoware':
        return {
          name: 'Advoware',
          bgColor: 'bg-blue-600',
          hoverColor: 'hover:bg-blue-700',
          textColor: 'text-white'
        }
      case 'kleos':
        return {
          name: 'Kleos',
          bgColor: 'bg-blue-600',
          hoverColor: 'hover:bg-blue-700',
          textColor: 'text-white'
        }
      case 'ramicro':
        return {
          name: 'RA-MICRO',
          bgColor: 'bg-[#e40614]',
          hoverColor: 'hover:bg-[#cc0512]',
          textColor: 'text-white'
        }
      case 'advolux':
        return {
          name: 'Advolux',
          bgColor: 'bg-[#fa6027]',
          hoverColor: 'hover:bg-[#e55622]',
          textColor: 'text-white'
        }
      case 'jurnodes':
        return {
          name: 'JurNodes',
          bgColor: 'bg-white border border-gray-300',
          hoverColor: 'hover:bg-gray-100',
          textColor: 'text-gray-900'
        }
      case 'datev':
        return {
          name: 'DATEV',
          bgColor: 'bg-[#0066cc]',
          hoverColor: 'hover:bg-[#0052a3]',
          textColor: 'text-white'
        }
      case 'lawfirm':
        return {
          name: 'Law Firm',
          bgColor: 'bg-gray-900',
          hoverColor: 'hover:bg-black',
          textColor: 'text-white'
        }
      default:
        return {
          name: integration,
          bgColor: 'bg-gray-500',
          hoverColor: 'hover:bg-gray-600',
          textColor: 'text-white'
        }
    }
  }
</script>

<template>
  <div class="flex flex-wrap gap-2">
    <!-- Actaport Conflict of Interest Button (always separate) -->
    <Button
      v-if="integrations.actaport"
      variant="default"
      class="flex h-9 gap-2 bg-gray-900 text-white hover:bg-black"
      @click="openActaportCollisionModal"
    >
      <img
        src="https://s3-eu-central-1.ionoscloud.com/jupus/images/actaport_logo.png"
        style="height: 12px; padding-right: 15px"
      />
      {{ t('conflictOfInterest') }}
    </Button>

    <!-- Dropdown for multiple integrations -->
    <DropdownMenu v-if="shouldShowDropdown">
      <DropdownMenuTrigger as-child>
        <Button
          variant="default"
          class="flex h-9 gap-2 bg-gray-900 text-white hover:bg-black"
        >
          {{ t('createCaseGeneral') }}
          <ChevronDown class="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent class="w-56">
        <!-- Actaport Case Creation/View -->
        <DropdownMenuItem
          v-if="integrations.actaport"
          @click="
            integrationStates.actaport.hasCase
              ? openActaportCase(integrationStates.actaport.caseId)
              : exportMandate('actaport')
          "
        >
          <img
            src="https://s3-eu-central-1.ionoscloud.com/jupus/images/actaport_logo.png"
            style="height: 12px; margin-right: 8px"
          />
          {{
            integrationStates.actaport.hasCase
              ? 'Aktenzeichen #' + integrationStates.actaport.caseId
              : t('createCase', { integration: 'Actaport' })
          }}
        </DropdownMenuItem>

        <!-- Law Firm Software Export -->
        <DropdownMenuItem
          v-if="integrations.lawFirmSoftwareExport"
          @click="exportMandate('law-firm-software')"
        >
          {{ t('exportFile', { integration: 'Law Firm' }) }}
        </DropdownMenuItem>

        <!-- DATEV Integration -->
        <DropdownMenuItem
          v-if="integrations.datev"
          @click="exportMandate('datev')"
        >
          {{ t('exportFile', { integration: 'DATEV' }) }}
        </DropdownMenuItem>

        <!-- Advolux Integration -->
        <DropdownMenuItem
          v-if="integrations.advolux"
          @click="exportMandate('advolux')"
        >
          {{ t('exportFile', { integration: 'Advolux' }) }}
        </DropdownMenuItem>

        <!-- RA-MICRO Integration -->
        <DropdownMenuItem
          v-if="integrations.raMicro"
          @click="isOmaDownload ? exportMandate('ramicro') : exportMandate('ramicroapi')"
        >
          {{ t(isOmaDownload ? 'exportFile' : 'createCase', { integration: 'RA-MICRO' }) }}
        </DropdownMenuItem>

        <!-- Advoware Integration -->
        <DropdownMenuItem
          v-if="integrations.advoware"
          @click="exportMandate('advoware')"
        >
          {{
            integrationStates.advoware.hasCase
              ? 'Aktenzeichen #' + integrationStates.advoware.caseId
              : t('createCase', { integration: 'Advoware' })
          }}
        </DropdownMenuItem>

        <!-- Kleos Integration -->
        <DropdownMenuItem
          v-if="integrations.kleos"
          @click="exportMandate('kleos')"
        >
          {{
            integrationStates.kleos.hasCase
              ? 'Aktenzeichen ' + integrationStates.kleos.caseId
              : t('createCase', { integration: 'Kleos' })
          }}
        </DropdownMenuItem>

        <!-- JurNodes Integration -->
        <DropdownMenuItem
          v-if="integrations.jurNodes"
          @click="exportMandate('jurnodes')"
        >
          {{ t('createCase', { integration: 'JurNodes' }) }}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Individual buttons when only 1 integration is enabled -->
    <template v-else>
      <!-- Law Firm Software Export -->
      <Button
        v-if="integrations.lawFirmSoftwareExport"
        variant="default"
        class="flex h-9 gap-2 bg-gray-900 text-white hover:bg-black"
        @click="exportMandate('law-firm-software')"
      >
        {{ t('exportFile', { integration: 'Law Firm' }) }}
      </Button>

      <!-- Actaport Case Creation/View (when only one integration) -->
      <Button
        v-if="integrations.actaport"
        variant="default"
        class="flex h-9 gap-2 bg-gray-900 text-white hover:bg-black"
        @click="
          integrationStates.actaport.hasCase
            ? openActaportCase(integrationStates.actaport.caseId)
            : exportMandate('actaport')
        "
      >
        <img
          src="https://s3-eu-central-1.ionoscloud.com/jupus/images/actaport_logo.png"
          style="height: 12px; padding-right: 15px"
        />
        <div class="flex items-center gap-2">
          {{
            integrationStates.actaport.hasCase
              ? 'Aktenzeichen #' + integrationStates.actaport.caseId
              : t('createCase', { integration: 'Actaport' })
          }}
        </div>
      </Button>

      <!-- DATEV Integration -->
      <Button
        v-if="integrations.datev"
        variant="default"
        class="flex grow gap-2 bg-[#0066cc] text-white hover:bg-[#0052a3]"
        @click="exportMandate('datev')"
      >
        <div class="flex items-center gap-2">
          {{ t('exportFile', { integration: 'DATEV' }) }}
        </div>
      </Button>

      <!-- Advolux Integration -->
      <Button
        v-if="integrations.advolux"
        variant="default"
        class="flex h-9 gap-2 bg-[#fa6027] text-white hover:bg-[#e55622]"
        @click="exportMandate('advolux')"
      >
        <div class="flex items-center gap-2">
          {{ t('exportFile', { integration: 'Advolux' }) }}
        </div>
      </Button>

      <!-- RA-MICRO Integration -->
      <Button
        v-if="integrations.raMicro"
        variant="default"
        class="flex h-9 gap-2 bg-[#e40614] text-white hover:bg-[#cc0512]"
        @click="isOmaDownload ? exportMandate('ramicro') : exportMandate('ramicroapi')"
      >
        <div class="flex items-center gap-2">
          {{ t(isOmaDownload ? 'exportFile' : 'createCase', { integration: 'RA-MICRO' }) }}
        </div>
      </Button>

      <!-- Advoware Integration -->
      <Button
        v-if="integrations.advoware"
        variant="default"
        class="flex h-9 gap-2 bg-blue-600 text-white hover:bg-blue-700"
        @click="exportMandate('advoware')"
      >
        <div class="flex items-center gap-2">
          {{
            integrationStates.advoware.hasCase
              ? 'Aktenzeichen #' + integrationStates.advoware.caseId
              : t('createCase', { integration: 'Advoware' })
          }}
        </div>
      </Button>

      <!-- Kleos Integration -->
      <Button
        v-if="integrations.kleos"
        variant="default"
        class="flex h-9 gap-2 bg-blue-600 text-white hover:bg-blue-700"
        @click="exportMandate('kleos')"
      >
        <div class="flex items-center gap-2">
          {{
            integrationStates.kleos.hasCase
              ? 'Aktenzeichen ' + integrationStates.kleos.caseId
              : t('createCase', { integration: 'Kleos' })
          }}
        </div>
      </Button>

      <!-- JurNodes Integration -->
      <Button
        v-if="integrations.jurNodes"
        variant="default"
        class="flex h-9 gap-2 border border-gray-300 bg-white text-gray-900 hover:bg-gray-100"
        @click="exportMandate('jurnodes')"
      >
        <div class="flex items-center gap-2">
          {{ t('createCase', { integration: 'JurNodes' }) }}
        </div>
      </Button>
    </template>
  </div>

  <!-- Actaport Collision Modal -->
  <ActaportCollisionModal
    v-if="integrations.actaport"
    v-model:show="showActaportCollisionModal"
    :case-uuid="props.caseRecord?.uuid"
  />
</template>
